/**
 * Topology Visualization Component
 *
 * Renders network topology from DOT format using d3-graphviz.
 * Provides interactive visualization of the cluster network design.
 */

import { useEffect, useRef, useState } from 'react';
import { graphviz } from 'd3-graphviz';
import * as d3 from 'd3';
import { useStore } from '../../store/store';
import { generateDOT } from '../../io/dot';

export function TopologyVisualization() {
  const graphRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string>('');
  const [isRendering, setIsRendering] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [options, setOptions] = useState({
    includeInfiniBand: true,
    includeEthernet: true,
    includeOOB: false,
    maxNodesToShow: 50,
    colorByLayer: true,
    layoutDirection: 'TB' as 'TB' | 'LR' | 'BT' | 'RL',
  });

  const bom = useStore((state) => state.bom);
  const configuration = useStore((state) => state.configuration);

  // Debug BOM access
  console.log('[TopologyVisualization] BOM from store:', {
    hasBOM: !!bom,
    nodeCount: bom?.summary?.compute?.totalNodes,
  });

  const handlePopOut = () => {
    if (!bom) return;

    try {
      // Pre-generate all DOT variants for layer toggling
      const dotVariants = {
        '111': generateDOT(bom, configuration, { ...options, includeInfiniBand: true, includeEthernet: true, includeOOB: true }),
        '110': generateDOT(bom, configuration, { ...options, includeInfiniBand: true, includeEthernet: true, includeOOB: false }),
        '101': generateDOT(bom, configuration, { ...options, includeInfiniBand: true, includeEthernet: false, includeOOB: true }),
        '100': generateDOT(bom, configuration, { ...options, includeInfiniBand: true, includeEthernet: false, includeOOB: false }),
        '011': generateDOT(bom, configuration, { ...options, includeInfiniBand: false, includeEthernet: true, includeOOB: true }),
        '010': generateDOT(bom, configuration, { ...options, includeInfiniBand: false, includeEthernet: true, includeOOB: false }),
        '001': generateDOT(bom, configuration, { ...options, includeInfiniBand: false, includeEthernet: false, includeOOB: true }),
        '000': generateDOT(bom, configuration, { ...options, includeInfiniBand: false, includeEthernet: false, includeOOB: false })
      };

      // Serialize BOM data for force-directed graph
      const bomDataStr = JSON.stringify(bom);
      const dotVariantsStr = JSON.stringify(dotVariants);

      // Open new window with larger size
      const popoutWindow = window.open('', 'TopologyViewer', 'width=1400,height=900,menubar=no,toolbar=no,location=no,status=no');

      if (!popoutWindow) {
        alert('Please allow pop-ups for this site to use the pop-out feature.');
        return;
      }

      // Write HTML content to the new window
      popoutWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Network Topology - ${bom.summary.compute.totalNodes} nodes</title>
          <script src="https://unpkg.com/@hpcc-js/wasm@2.13.0/dist/graphviz.umd.js"></script>
          <script src="https://unpkg.com/d3@7.8.5/dist/d3.min.js"></script>
          <script src="https://unpkg.com/d3-graphviz@5.0.2/build/d3-graphviz.min.js"></script>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
              background: #f9fafb;
              overflow: hidden;
            }
            #controls {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              background: white;
              border-bottom: 2px solid #e5e7eb;
              padding: 12px 20px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              z-index: 1000;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            #controls h1 {
              font-size: 18px;
              font-weight: 600;
              color: #111827;
            }
            #controls .stats {
              font-size: 14px;
              color: #6b7280;
            }
            #zoom-controls {
              display: flex;
              gap: 8px;
            }
            button {
              background: #4f46e5;
              color: white;
              border: none;
              padding: 8px 16px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 500;
              transition: background 0.2s;
            }
            button:hover {
              background: #4338ca;
            }
            button.secondary {
              background: #6b7280;
            }
            button.secondary:hover {
              background: #4b5563;
            }
            #layer-controls {
              display: flex;
              gap: 16px;
              align-items: center;
              margin-right: 16px;
              padding-right: 16px;
              border-right: 1px solid #e5e7eb;
            }
            #layer-controls label {
              display: flex;
              align-items: center;
              gap: 6px;
              font-size: 14px;
              color: #374151;
              cursor: pointer;
            }
            #layer-controls input[type="checkbox"] {
              width: 16px;
              height: 16px;
              cursor: pointer;
            }
            #layout-toggle {
              display: flex;
              gap: 4px;
              margin-right: 16px;
              padding-right: 16px;
              border-right: 1px solid #e5e7eb;
            }
            #layout-toggle button {
              padding: 6px 12px;
              font-size: 13px;
            }
            #layout-toggle button.active {
              background: #4f46e5;
            }
            #layout-toggle button:not(.active) {
              background: #9ca3af;
            }
            #graph {
              position: absolute;
              top: 60px;
              left: 0;
              right: 0;
              bottom: 0;
              width: 100%;
              height: calc(100vh - 60px);
              background: white;
              display: flex;
              justify-content: center;
              align-items: flex-start;
            }
            .loading {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              text-align: center;
              font-size: 16px;
              color: #6b7280;
            }
            .spinner {
              border: 4px solid #f3f4f6;
              border-top: 4px solid #4f46e5;
              border-radius: 50%;
              width: 40px;
              height: 40px;
              animation: spin 1s linear infinite;
              margin: 0 auto 12px;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            #legend {
              position: fixed;
              bottom: 20px;
              right: 20px;
              background: white;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 12px;
              box-shadow: 0 4px 6px rgba(0,0,0,0.1);
              font-size: 12px;
              max-width: 300px;
            }
            #legend h3 {
              font-size: 13px;
              font-weight: 600;
              margin-bottom: 8px;
              color: #111827;
            }
            .legend-item {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 6px;
            }
            .legend-color {
              width: 16px;
              height: 16px;
              border: 1px solid #9ca3af;
            }
          </style>
        </head>
        <body>
          <div id="controls">
            <div>
              <h1>Network Topology Visualization</h1>
              <div class="stats">${bom.summary.compute.totalNodes} nodes, ${bom.summary.network.totalSwitches} switches, ${bom.summary.network.totalCables} cables</div>
            </div>
            <div style="display: flex; gap: 16px; align-items: center;">
              <div id="layout-toggle">
                <button id="btn-static" class="active" onclick="switchLayout('static')">Static</button>
                <button id="btn-interactive" onclick="switchLayout('interactive')">Interactive</button>
              </div>
              <div id="layer-controls">
                <label>
                  <input type="checkbox" id="check-ib" ${options.includeInfiniBand ? 'checked' : ''} onchange="handleLayerChange()">
                  InfiniBand
                </label>
                <label>
                  <input type="checkbox" id="check-eth" ${options.includeEthernet ? 'checked' : ''} onchange="handleLayerChange()">
                  Ethernet
                </label>
                <label>
                  <input type="checkbox" id="check-oob" ${options.includeOOB ? 'checked' : ''} onchange="handleLayerChange()">
                  OOB
                </label>
              </div>
              <div id="zoom-controls">
                <button onclick="zoomIn()">Zoom In (+)</button>
                <button onclick="zoomOut()">Zoom Out (-)</button>
                <button onclick="resetZoom()">Reset View</button>
                <button class="secondary" onclick="window.print()">Print/PDF</button>
              </div>
            </div>
          </div>

          <div id="graph">
            <div class="loading">
              <div class="spinner"></div>
              <div>Rendering topology...</div>
            </div>
          </div>

          <div id="legend">
            <h3>Legend</h3>
            <div class="legend-item">
              <div class="legend-color" style="background: #bfdbfe;"></div>
              <span>Compute Nodes</span>
            </div>
            <div class="legend-item">
              <div class="legend-color" style="background: #bbf7d0; border-radius: 50%;"></div>
              <span>Leaf Switches</span>
            </div>
            <div class="legend-item">
              <div class="legend-color" style="background: #fef08a; transform: rotate(45deg);"></div>
              <span>Spine Switches</span>
            </div>
            <div class="legend-item">
              <div class="legend-color" style="background: #fbcfe8;"></div>
              <span>Core Switches</span>
            </div>
            <div class="legend-item">
              <div class="legend-color" style="background: #e5e7eb;"></div>
              <span>OOB/Management</span>
            </div>
          </div>

          <script>
            // BOM data for force-directed graph
            const bomData = ${bomDataStr};

            // Layout mode state
            let currentLayout = 'static';  // 'static' or 'interactive'

            // Pre-generated DOT variants for layer combinations
            const dotVariants = ${dotVariantsStr};

            let currentDotContent = dotVariants['${options.includeInfiniBand ? '1' : '0'}${options.includeEthernet ? '1' : '0'}${options.includeOOB ? '1' : '0'}'];
            let graphvizInstance;

            function renderTopology() {
              const graphDiv = document.getElementById('graph');

              try {
                // Show loading indicator
                document.querySelector('.loading').style.display = 'block';

                graphvizInstance = d3.select("#graph")
                  .graphviz()
                  .engine('dot')
                  .width(window.innerWidth)
                  .height(window.innerHeight - 60)
                  .fit(true)
                  .zoom(true)
                  .transition(function() { return d3.transition().duration(0); })  // Zero-duration transition
                  .tweenShapes(false)  // Disable shape tweening
                  .tweenPaths(false)  // Disable path tweening/interpolation
                  .convertEqualSidedPolygons(false)  // Don't convert polygons
                  .renderDot(currentDotContent)
                  .on('end', function() {
                    // Post-process: Convert all edge paths to straight lines
                    const svg = document.querySelector('#graph svg');
                    if (svg) {
                      const edges = svg.querySelectorAll('.edge path');
                      edges.forEach(function(path) {
                        const d = path.getAttribute('d');
                        if (d && (d.includes('C') || d.includes('c'))) {
                          // Convert bezier curves to straight lines
                          console.log('Original path:', d);

                          // Extract start point (M command)
                          const startMatch = d.match(/M\\s*([-\\d.]+)[,\\s]+([-\\d.]+)/);
                          // Extract final end point - last coordinate pair in the path
                          const allNumbers = d.match(/([-\\d.]+)/g);

                          if (startMatch && allNumbers && allNumbers.length >= 4) {
                            const x1 = startMatch[1];
                            const y1 = startMatch[2];
                            // Last two numbers are the end coordinates
                            const x2 = allNumbers[allNumbers.length - 2];
                            const y2 = allNumbers[allNumbers.length - 1];
                            const newPath = 'M' + x1 + ',' + y1 + ' L' + x2 + ',' + y2;
                            console.log('Converted to:', newPath);
                            path.setAttribute('d', newPath);
                          }
                        }
                      });
                    }
                    document.querySelector('.loading').style.display = 'none';
                  });
              } catch (err) {
                console.error('Rendering error:', err);
                document.querySelector('.loading').innerHTML =
                  '<div style="color: #dc2626;">Error rendering topology: ' + err.message + '</div>';
              }
            }

            function zoomIn() {
              const svg = document.querySelector('#graph svg');
              if (svg) {
                const currentScale = parseFloat(svg.style.transform?.match(/scale\\(([^)]+)\\)/)?.[1] || 1);
                svg.style.transform = 'scale(' + (currentScale * 1.2) + ')';
              }
            }

            function zoomOut() {
              const svg = document.querySelector('#graph svg');
              if (svg) {
                const currentScale = parseFloat(svg.style.transform?.match(/scale\\(([^)]+)\\)/)?.[1] || 1);
                svg.style.transform = 'scale(' + (currentScale / 1.2) + ')';
              }
            }

            function resetZoom() {
              if (graphvizInstance) {
                graphvizInstance.resetZoom();
              }
            }

            function handleLayerChange() {
              // Get current checkbox states
              const includeIB = document.getElementById('check-ib').checked;
              const includeETH = document.getElementById('check-eth').checked;
              const includeOOB = document.getElementById('check-oob').checked;

              if (currentLayout === 'static') {
                // Build key for dot variant lookup
                const key = (includeIB ? '1' : '0') + (includeETH ? '1' : '0') + (includeOOB ? '1' : '0');

                // Update current DOT content
                currentDotContent = dotVariants[key];

                // Clear and re-render
                const graphDiv = document.getElementById('graph');
                graphDiv.innerHTML = '<div class="loading"><div class="spinner"></div><div>Rendering topology...</div></div>';

                // Re-render with new DOT content
                setTimeout(function() {
                  renderTopology();
                }, 50);
              } else {
                // For interactive mode, just re-render with new layer settings
                renderForceGraph(includeIB, includeETH, includeOOB);
              }
            }

            function switchLayout(mode) {
              currentLayout = mode;

              // Update button states
              document.getElementById('btn-static').classList.toggle('active', mode === 'static');
              document.getElementById('btn-interactive').classList.toggle('active', mode === 'interactive');

              // Clear and re-render
              const graphDiv = document.getElementById('graph');
              graphDiv.innerHTML = '<div class="loading"><div class="spinner"></div><div>Rendering topology...</div></div>';

              if (mode === 'static') {
                setTimeout(function() {
                  renderTopology();
                }, 50);
              } else {
                const includeIB = document.getElementById('check-ib').checked;
                const includeETH = document.getElementById('check-eth').checked;
                const includeOOB = document.getElementById('check-oob').checked;
                setTimeout(function() {
                  renderForceGraph(includeIB, includeETH, includeOOB);
                }, 50);
              }
            }

            function renderForceGraph(includeIB, includeETH, includeOOB) {
              const graphDiv = document.getElementById('graph');
              graphDiv.innerHTML = '';

              // Convert BOM data to nodes and links
              const nodes = [];
              const links = [];

              // Color scheme
              const colors = {
                compute: '#bfdbfe',
                ib_leaf: '#bbf7d0',
                ib_spine: '#fef08a',
                eth_leaf: '#a7f3d0',
                eth_spine: '#fde68a',
                oob: '#e5e7eb'
              };

              // Add compute nodes
              const nodeCount = Math.min(bomData.summary.compute.totalNodes, 50); // Limit for performance
              for (let i = 0; i < nodeCount; i++) {
                nodes.push({
                  id: 'node_' + i,
                  type: 'compute',
                  label: 'Node ' + i,
                  layer: 'compute',
                  color: colors.compute,
                  radius: 8
                });
              }

              // Add InfiniBand switches if enabled
              if (includeIB && bomData.summary.network.switchBreakdown) {
                const ibLeafCount = bomData.summary.network.switchBreakdown.eastWestLeaf || 0;
                const ibSpineCount = bomData.summary.network.switchBreakdown.eastWestSpine || 0;

                for (let i = 0; i < ibLeafCount; i++) {
                  const nodeId = 'ib_leaf_' + i;
                  nodes.push({
                    id: nodeId,
                    type: 'ib_leaf',
                    label: 'IB Leaf ' + i,
                    layer: 'ib',
                    color: colors.ib_leaf,
                    radius: 12
                  });

                  // Connect to compute nodes
                  const nodesPerLeaf = Math.ceil(nodeCount / ibLeafCount);
                  for (let j = 0; j < nodesPerLeaf && (i * nodesPerLeaf + j) < nodeCount; j++) {
                    links.push({
                      source: 'node_' + (i * nodesPerLeaf + j),
                      target: nodeId,
                      layer: 'ib'
                    });
                  }
                }

                for (let i = 0; i < ibSpineCount; i++) {
                  const nodeId = 'ib_spine_' + i;
                  nodes.push({
                    id: nodeId,
                    type: 'ib_spine',
                    label: 'IB Spine ' + i,
                    layer: 'ib',
                    color: colors.ib_spine,
                    radius: 15
                  });

                  // Connect to all leaf switches
                  for (let j = 0; j < ibLeafCount; j++) {
                    links.push({
                      source: 'ib_leaf_' + j,
                      target: nodeId,
                      layer: 'ib'
                    });
                  }
                }
              }

              // Add Ethernet switches if enabled
              if (includeETH && bomData.summary.network.switchBreakdown) {
                const ethLeafCount = bomData.summary.network.switchBreakdown.northSouthLeaf || 0;
                const ethSpineCount = bomData.summary.network.switchBreakdown.northSouthSpine || 0;

                for (let i = 0; i < ethLeafCount; i++) {
                  const nodeId = 'eth_leaf_' + i;
                  nodes.push({
                    id: nodeId,
                    type: 'eth_leaf',
                    label: 'ETH Leaf ' + i,
                    layer: 'eth',
                    color: colors.eth_leaf,
                    radius: 12
                  });

                  // Connect to compute nodes
                  const nodesPerLeaf = Math.ceil(nodeCount / ethLeafCount);
                  for (let j = 0; j < nodesPerLeaf && (i * nodesPerLeaf + j) < nodeCount; j++) {
                    links.push({
                      source: 'node_' + (i * nodesPerLeaf + j),
                      target: nodeId,
                      layer: 'eth'
                    });
                  }
                }

                for (let i = 0; i < ethSpineCount; i++) {
                  const nodeId = 'eth_spine_' + i;
                  nodes.push({
                    id: nodeId,
                    type: 'eth_spine',
                    label: 'ETH Spine ' + i,
                    layer: 'eth',
                    color: colors.eth_spine,
                    radius: 15
                  });

                  // Connect to all leaf switches
                  for (let j = 0; j < ethLeafCount; j++) {
                    links.push({
                      source: 'eth_leaf_' + j,
                      target: nodeId,
                      layer: 'eth'
                    });
                  }
                }
              }

              // Add OOB switches if enabled
              if (includeOOB && bomData.summary.network.switchBreakdown) {
                const oobCount = bomData.summary.network.switchBreakdown.management || 0;

                // For OOB, assume a simple leaf-spine with half as leaf and half as spine
                const oobLeafCount = Math.ceil(oobCount / 2);
                const oobSpineCount = Math.floor(oobCount / 2);

                for (let i = 0; i < oobLeafCount; i++) {
                  const nodeId = 'oob_leaf_' + i;
                  nodes.push({
                    id: nodeId,
                    type: 'oob',
                    label: 'OOB Leaf ' + i,
                    layer: 'oob',
                    color: colors.oob,
                    radius: 10
                  });

                  // Connect to compute nodes
                  const nodesPerLeaf = Math.ceil(nodeCount / oobLeafCount);
                  for (let j = 0; j < nodesPerLeaf && (i * nodesPerLeaf + j) < nodeCount; j++) {
                    links.push({
                      source: 'node_' + (i * nodesPerLeaf + j),
                      target: nodeId,
                      layer: 'oob'
                    });
                  }
                }

                for (let i = 0; i < oobSpineCount; i++) {
                  const nodeId = 'oob_spine_' + i;
                  nodes.push({
                    id: nodeId,
                    type: 'oob',
                    label: 'OOB Spine ' + i,
                    layer: 'oob',
                    color: colors.oob,
                    radius: 12
                  });

                  // Connect to all leaf switches
                  for (let j = 0; j < oobLeafCount; j++) {
                    links.push({
                      source: 'oob_leaf_' + j,
                      target: nodeId,
                      layer: 'oob'
                    });
                  }
                }
              }

              // Set up SVG
              const width = window.innerWidth;
              const height = window.innerHeight - 60;

              const svg = d3.select('#graph')
                .append('svg')
                .attr('width', width)
                .attr('height', height);

              const g = svg.append('g');

              // Add zoom behavior
              const zoom = d3.zoom()
                .scaleExtent([0.1, 10])
                .on('zoom', function(event) {
                  g.attr('transform', event.transform);
                });

              svg.call(zoom);

              // Create force simulation
              const simulation = d3.forceSimulation(nodes)
                .force('link', d3.forceLink(links).id(function(d) { return d.id; }).distance(100))
                .force('charge', d3.forceManyBody().strength(-300))
                .force('center', d3.forceCenter(width / 2, height / 2))
                .force('collision', d3.forceCollide().radius(function(d) { return d.radius + 5; }));

              // Create links
              const link = g.append('g')
                .attr('class', 'links')
                .selectAll('line')
                .data(links)
                .enter().append('line')
                .attr('stroke', '#999')
                .attr('stroke-opacity', 0.6)
                .attr('stroke-width', 2);

              // Create nodes
              const node = g.append('g')
                .attr('class', 'nodes')
                .selectAll('circle')
                .data(nodes)
                .enter().append('circle')
                .attr('r', function(d) { return d.radius; })
                .attr('fill', function(d) { return d.color; })
                .attr('stroke', '#333')
                .attr('stroke-width', 2)
                .call(d3.drag()
                  .on('start', function(event, d) {
                    if (!event.active) simulation.alphaTarget(0.3).restart();
                    d.fx = d.x;
                    d.fy = d.y;
                  })
                  .on('drag', function(event, d) {
                    d.fx = event.x;
                    d.fy = event.y;
                  })
                  .on('end', function(event, d) {
                    if (!event.active) simulation.alphaTarget(0);
                    d.fx = null;
                    d.fy = null;
                  }));

              // Add labels
              const label = g.append('g')
                .attr('class', 'labels')
                .selectAll('text')
                .data(nodes)
                .enter().append('text')
                .text(function(d) { return d.label; })
                .attr('font-size', 10)
                .attr('dx', 12)
                .attr('dy', 4);

              // Add tooltips
              node.append('title')
                .text(function(d) { return d.label + ' (' + d.type + ')'; });

              // Update positions on tick
              simulation.on('tick', function() {
                link
                  .attr('x1', function(d) { return d.source.x; })
                  .attr('y1', function(d) { return d.source.y; })
                  .attr('x2', function(d) { return d.target.x; })
                  .attr('y2', function(d) { return d.target.y; });

                node
                  .attr('cx', function(d) { return d.x; })
                  .attr('cy', function(d) { return d.y; });

                label
                  .attr('x', function(d) { return d.x; })
                  .attr('y', function(d) { return d.y; });
              });

              // Hide loading indicator
              document.querySelector('.loading').style.display = 'none';
            }

            // Handle window resize
            window.addEventListener('resize', function() {
              if (graphvizInstance) {
                graphvizInstance
                  .width(window.innerWidth)
                  .height(window.innerHeight - 60)
                  .fit(true)
                  .render();
              }
            });

            // Keyboard shortcuts
            document.addEventListener('keydown', function(e) {
              if (e.key === '+' || e.key === '=') {
                zoomIn();
              } else if (e.key === '-') {
                zoomOut();
              } else if (e.key === '0') {
                resetZoom();
              }
            });

            // Initialize after scripts load
            window.addEventListener('load', function() {
              setTimeout(renderTopology, 100);
            });
          </script>
        </body>
        </html>
      `);

      popoutWindow.document.close();
    } catch (error) {
      console.error('Pop-out error:', error);
      alert('Failed to open topology in new window: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const renderGraph = async () => {
    if (!graphRef.current || !bom) {
      return;
    }

    setIsRendering(true);
    setError('');

    try {
      // Generate DOT content
      const dotContent = generateDOT(bom, configuration, {
        includeComments: false,
        includeMetadata: false,
        includeInfiniBand: options.includeInfiniBand,
        includeEthernet: options.includeEthernet,
        includeOOB: options.includeOOB,
        maxNodesToShow: options.maxNodesToShow,
        colorByLayer: options.colorByLayer,
        layoutDirection: options.layoutDirection,
      });

      // Clear existing graph
      graphRef.current.innerHTML = '';

      // Render with d3-graphviz
      graphviz(graphRef.current, {
        fit: true,
        width: graphRef.current.clientWidth,
        height: 600,
        zoom: true,
        useWorker: false,
        engine: 'dot',
      })
        .engine('dot')
        .transition(() => d3.transition().duration(0))  // Zero-duration transition
        .tweenShapes(false)  // Disable shape tweening
        .tweenPaths(false)  // Disable path tweening/interpolation
        .convertEqualSidedPolygons(false)  // Don't convert polygons
        .renderDot(dotContent)
        .on('end', () => {
          // Post-process: Convert all edge paths to straight lines
          const svg = graphRef.current?.querySelector('svg');
          if (svg) {
            const edges = svg.querySelectorAll('.edge path');
            edges.forEach((path: SVGPathElement) => {
              const d = path.getAttribute('d');
              if (d && (d.includes('C') || d.includes('c'))) {
                // Convert bezier curves to straight lines
                // Bezier curve format: M x1,y1 C cx1,cy1 cx2,cy2 x2,y2
                // We want just: M x1,y1 L x2,y2
                console.log('Original path:', d);

                // Extract start point (M command)
                const startMatch = d.match(/M\s*([-\d.]+)[,\s]+([-\d.]+)/);
                // Extract final end point - last coordinate pair in the path
                const allNumbers = d.match(/([-\d.]+)/g);

                if (startMatch && allNumbers && allNumbers.length >= 4) {
                  const x1 = startMatch[1];
                  const y1 = startMatch[2];
                  // Last two numbers are the end coordinates
                  const x2 = allNumbers[allNumbers.length - 2];
                  const y2 = allNumbers[allNumbers.length - 1];
                  const newPath = `M${x1},${y1} L${x2},${y2}`;
                  console.log('Converted to:', newPath);
                  path.setAttribute('d', newPath);
                }
              }
            });
          }
          setIsRendering(false);
        });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to render topology');
      setIsRendering(false);
    }
  };

  // Render on mount and when options change
  useEffect(() => {
    renderGraph();
  }, [bom, options]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      renderGraph();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [bom, options]);

  if (!bom) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg border border-gray-300">
        <div className="text-center">
          <div className="text-gray-600 mb-2">No topology data available</div>
          <div className="text-sm text-gray-500">
            Topology will be generated automatically from your configuration
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-bold text-gray-900">Network Topology Visualization</h3>
          <div className="flex gap-2">
            <button
              onClick={handlePopOut}
              className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
              title="Open in new window with enhanced zoom controls"
            >
              ↗ Pop Out
            </button>
            <button
              onClick={() => setShowOptions(!showOptions)}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              {showOptions ? '▼ Hide Options' : '▶ Show Options'}
            </button>
          </div>
        </div>

        {showOptions && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 pt-3 border-t border-gray-200">
            {/* Network Types */}
            <div>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={options.includeInfiniBand}
                  onChange={(e) =>
                    setOptions({ ...options, includeInfiniBand: e.target.checked })
                  }
                  className="mr-2"
                />
                InfiniBand (East-West)
              </label>
            </div>

            <div>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={options.includeEthernet}
                  onChange={(e) =>
                    setOptions({ ...options, includeEthernet: e.target.checked })
                  }
                  className="mr-2"
                />
                Ethernet (North-South)
              </label>
            </div>

            <div>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={options.includeOOB}
                  onChange={(e) => setOptions({ ...options, includeOOB: e.target.checked })}
                  className="mr-2"
                />
                OOB Management
              </label>
            </div>

            {/* Display Options */}
            <div>
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={options.colorByLayer}
                  onChange={(e) =>
                    setOptions({ ...options, colorByLayer: e.target.checked })
                  }
                  className="mr-2"
                />
                Color by Layer
              </label>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Max Nodes to Show
              </label>
              <input
                type="number"
                value={options.maxNodesToShow}
                onChange={(e) =>
                  setOptions({ ...options, maxNodesToShow: parseInt(e.target.value) })
                }
                min="10"
                max="500"
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Layout Direction
              </label>
              <select
                value={options.layoutDirection}
                onChange={(e) =>
                  setOptions({
                    ...options,
                    layoutDirection: e.target.value as 'TB' | 'LR' | 'BT' | 'RL',
                  })
                }
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
              >
                <option value="TB">Top to Bottom</option>
                <option value="LR">Left to Right</option>
                <option value="BT">Bottom to Top</option>
                <option value="RL">Right to Left</option>
              </select>
            </div>
          </div>
        )}

        <div className="text-xs text-gray-600">
          <strong>Summary:</strong> {bom.summary.compute.totalNodes} nodes,{' '}
          {bom.summary.network.totalSwitches} switches, {bom.summary.network.totalCables} cables
          {options.maxNodesToShow < bom.summary.compute.totalNodes && (
            <span className="text-orange-600">
              {' '}
              (showing {options.maxNodesToShow} nodes for performance)
            </span>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800 text-sm font-semibold">Visualization Error</div>
          <div className="text-red-700 text-sm mt-1">{error}</div>
        </div>
      )}

      {/* Loading Indicator */}
      {isRendering && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 rounded-lg shadow-lg">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <div className="text-sm text-gray-600 mt-2">Rendering topology...</div>
          </div>
        </div>
      )}

      {/* Graph Container */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div
          ref={graphRef}
          className="w-full flex justify-center items-start"
          style={{ minHeight: '600px', maxHeight: '800px' }}
        />
      </div>

      {/* Legend */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="text-sm font-semibold text-blue-900 mb-2">Legend</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-blue-200 border border-gray-400 mr-2"></div>
            <span>Compute Nodes</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-200 border border-gray-400 rounded-full mr-2"></div>
            <span>Leaf Switches</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-yellow-200 border border-gray-400 mr-2" style={{ transform: 'rotate(45deg)' }}></div>
            <span>Spine Switches</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-pink-200 border border-gray-400 mr-2" style={{ clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }}></div>
            <span>Core Switches</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-gray-200 border border-gray-400 mr-2" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}></div>
            <span>OOB/Management</span>
          </div>
        </div>
        <div className="text-xs text-gray-600 mt-3">
          <strong>Tip:</strong> Use mouse wheel to zoom, drag to pan. Click nodes for details.
        </div>
      </div>
    </div>
  );
}
